import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import { adminApi } from '../api/admin';
import type { AdminUserInfo } from '../types/admin';
import { hasAnyRole } from '../auth/roleUtils';

const AdminUsersPageContent: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserInfo[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUsername, setDeleteUsername] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [filterInput, setFilterInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAdmin = hasAnyRole(user?.roles, 'ADMIN');
  const currentUserId = user?.sub;
  const hasFilter = filterQuery.trim().length > 0;

  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    filterDebounceRef.current = setTimeout(() => {
      setFilterQuery(filterInput.trim());
    }, 300);
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, [filterInput]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { users: list, totalCount: total } = await adminApi.getUsers({
          first: 0,
          max: 500,
          search: filterQuery || undefined,
        });
        setUsers(list);
        setTotalCount(total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [isAdmin, navigate, filterQuery]);

  const openDeleteModal = (u: AdminUserInfo) => {
    setDeleteUserId(u.id);
    setDeleteUsername(u.username);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteUserId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await adminApi.deleteUser(deleteUserId);
      setUsers(prev => prev.filter(u => u.id !== deleteUserId));
      setTotalCount(prev => (prev !== null ? Math.max(0, prev - 1) : null));
      setDeleteUserId(null);
      setDeleteUsername('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      const message = ax?.response?.data?.message ?? (err instanceof Error ? err.message : 'Failed to delete user');
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const displayName = (u: AdminUserInfo) => {
    const parts = [u.firstName, u.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="page page--narrow">
      <h1 className="page-title">Registered users</h1>
      {!loading && totalCount !== null && (
        <p
          className="admin-users-total"
          style={{
            fontSize: '1.1rem',
            fontWeight: 600,
            marginTop: 0,
            marginBottom: 'var(--spacing-sm)',
          }}
        >
          {hasFilter ? `Matching users: ${totalCount}` : `Total users: ${totalCount}`}
        </p>
      )}
      <p className="page-description">
        All users registered in Keycloak. Deleting a user permanently removes their account,
        posts, comments, messages, and notifications.
      </p>

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <section className="admin-users-filter" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <Input
          id="admin-users-filter"
          type="search"
          label="Filter by username or email"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          placeholder="e.g. johndoe or user@example.com"
          disabled={loading && users.length === 0}
        />
      </section>

      <section className="admin-users-list">
        {loading ? (
          <LoadingSpinner />
        ) : users.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            {hasFilter ? 'No users match your filter.' : 'No registered users found.'}
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {users.map((u) => {
              const isSelf = u.id === currentUserId;
              return (
                <li
                  key={u.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    borderBottom: '1px solid var(--border-color)',
                    gap: 'var(--spacing-md)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div>
                    <strong>{u.username}</strong>
                    {!u.enabled && (
                      <span
                        style={{
                          marginLeft: 'var(--spacing-sm)',
                          fontSize: '0.85em',
                          color: 'var(--text-muted)',
                        }}
                      >
                        (disabled)
                      </span>
                    )}
                    {displayName(u) && (
                      <span style={{ color: 'var(--text-muted)', marginLeft: 'var(--spacing-sm)', fontSize: '0.9em' }}>
                        {displayName(u)}
                      </span>
                    )}
                    {u.email && (
                      <span style={{ color: 'var(--text-muted)', marginLeft: 'var(--spacing-sm)', fontSize: '0.85em' }}>
                        {u.email}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => openDeleteModal(u)}
                    disabled={deleting || isSelf}
                    title={isSelf ? 'You cannot delete your own account' : undefined}
                  >
                    Delete
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Modal
        isOpen={deleteUserId !== null}
        onClose={() => !deleting && setDeleteUserId(null)}
        title="Delete user"
      >
        <p>
          Permanently delete <strong>{deleteUsername}</strong> and all of their posts, comments,
          messages, and notifications? This cannot be undone.
        </p>
        {deleteError && (
          <ErrorMessage message={deleteError} onDismiss={() => setDeleteError(null)} />
        )}
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
          <Button variant="outline" onClick={() => setDeleteUserId(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} isLoading={deleting} disabled={deleting}>
            Delete permanently
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export const AdminUsersPage: React.FC = () => (
  <ProtectedRoute>
    <AdminUsersPageContent />
  </ProtectedRoute>
);
